<template>
  <div v-if="show" class="calendar-popup shadow-2xl border-0 animate-fade-in" :class="{ 'calendar-fullscreen': isFullscreen }">
    <div class="cal-header d-flex justify-content-between align-items-center py-4 px-4">
      <div class="month-year">
        <h4 class="mb-0 fw-bold text-dark">{{ months[month] }}</h4>
        <p class="mb-0 text-muted small">{{ year }}</p>
      </div>
      <div class="nav-btns d-flex gap-2">
        <button @click="prevMonth" class="btn btn-icon-round"><i class="fa fa-chevron-left"></i></button>
        <button @click="nextMonth" class="btn btn-icon-round"><i class="fa fa-chevron-right"></i></button>
        <button @click="toggleFullscreen" class="btn btn-icon-round ms-2" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'">
          <i :class="isFullscreen ? 'fa fa-compress' : 'fa fa-expand'"></i>
        </button>
        <button @click="closeCalendar" class="btn btn-icon-round"><i class="fa fa-times"></i></button>
      </div>
    </div>
    <div class="cal-body px-4 pb-4">
      <div class="weekdays mb-3">
        <span v-for="d in days" :key="d" class="text-uppercase fw-bold">{{ d }}</span>
      </div>
      <div class="days-grid">
        <span v-for="n in startDay" :key="'empty'+n" class="empty"></span>
        <div v-for="d in daysInMonth" :key="d" class="day-wrapper" @mouseenter="hoveredDay = d" @mouseleave="hoveredDay = null">
          <span 
            :class="{ 
              'today': isToday(d), 
              'has-po': hasOrderOnDay(d),
              'clickable': hasOrderOnDay(d)
            }" 
            class="day-circle"
            @click="goToOrderList(d)"
          >
            {{ d }}
          </span>
          <!-- Tooltip -->
          <div v-if="hoveredDay === d && hasOrderOnDay(d)" class="po-tooltip">
            <div class="tooltip-header">
              <i class="fa fa-shopping-bag me-2"></i>
              <span class="fw-bold">{{ getOrdersOnDay(d).length }} PO</span>
            </div>
            <div class="tooltip-body">
              <div v-for="po in getOrdersOnDay(d).slice(0, 5)" :key="po.id" class="tooltip-item">
                <span class="tooltip-dot"></span>
                <span class="tooltip-text">{{ po.member_toko || po.member_nama || 'Toko #' + po.member_id }}</span>
              </div>
              <div v-if="getOrdersOnDay(d).length > 5" class="tooltip-more">
                +{{ getOrdersOnDay(d).length - 5 }} lainnya...
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useDataStore } from '@/store/dataStore';

export default {
  props: ['show'],
  data() {
    const d = new Date();
    return {
      month: d.getMonth(),
      year: d.getFullYear(),
      days: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      months: ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'],
      isFullscreen: false,
      hoveredDay: null,
      orders: []
    };
  },
  computed: {
    daysInMonth() { return new Date(this.year, this.month + 1, 0).getDate(); },
    startDay() { return new Date(this.year, this.month, 1).getDay(); }
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadOrders();
        }
      }
    }
  },
  methods: {
    async loadOrders() {
      try {
        const { orders, loadAllData, refreshData, state } = useDataStore();
        
        // Always ensure data is loaded when calendar opens
        if (!state.isLoaded) {
          await loadAllData();
        } else {
          // Refresh orders data to get latest
          await refreshData('orders');
        }
        
        this.orders = orders.value || [];
        console.log('Calendar loaded orders:', this.orders.length);
      } catch (e) {
        console.error('Failed to load orders for calendar', e);
      }
    },
    prevMonth() {
      if (this.month === 0) { this.month = 11; this.year--; }
      else this.month--;
    },
    nextMonth() {
      if (this.month === 11) { this.month = 0; this.year++; }
      else this.month++;
    },
    isToday(d) {
      const now = new Date();
      return d === now.getDate() && this.month === now.getMonth() && this.year === now.getFullYear();
    },
    hasOrderOnDay(day) {
      return this.getOrdersOnDay(day).length > 0;
    },
    getOrdersOnDay(day) {
      const targetDate = `${this.year}-${String(this.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      return this.orders.filter(o => {
        if (!o.tanggal_order) return false;
        return o.tanggal_order.startsWith(targetDate);
      });
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },
    closeCalendar() {
      this.isFullscreen = false;
      this.$emit('close');
    },
    goToOrderList(day) {
      if (!this.hasOrderOnDay(day)) return;
      const dateStr = `${this.year}-${String(this.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      this.closeCalendar();
      this.$router.push(`/purchases/order-list?date=${dateStr}`);
    }
  }
}
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}
.calendar-popup {
  position: absolute;
  top: 75px;
  right: 180px;
  width: 350px;
  background: #ffffff;
  border-radius: 24px;
  z-index: 2000;
  box-shadow: 0 20px 50px rgba(0,0,0,0.15);
}
.btn-icon-round {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f8f9fa;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: all 0.2s;
}
.btn-icon-round:hover {
  background: #28a745;
  color: white;
}
.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
}
.weekdays span {
  font-size: 11px;
  color: #adb5bd;
  letter-spacing: 1px;
}
.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 8px;
}
.day-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}
.day-circle {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  border-radius: 50%;
  transition: all 0.2s;
  cursor: pointer;
}
.day-circle:hover {
  background: #f1f3f5;
  color: #28a745;
}
.today {
  background: #28a745 !important;
  color: #ffffff !important;
  box-shadow: 0 4px 10px rgba(40, 167, 69, 0.3);
  font-weight: bold;
}
.empty { visibility: hidden; }

/* Fullscreen Mode */
.calendar-fullscreen {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  border-radius: 0 !important;
  z-index: 9999 !important;
  display: flex;
  flex-direction: column;
}
.calendar-fullscreen .cal-header {
  padding: 24px 40px !important;
}
.calendar-fullscreen .cal-header h4 {
  font-size: 2rem;
}
.calendar-fullscreen .cal-header p {
  font-size: 1rem;
}
.calendar-fullscreen .btn-icon-round {
  width: 44px;
  height: 44px;
  font-size: 14px;
}
.calendar-fullscreen .cal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 40px 40px !important;
}
.calendar-fullscreen .weekdays {
  margin-bottom: 20px !important;
}
.calendar-fullscreen .weekdays span {
  font-size: 14px;
}
.calendar-fullscreen .days-grid {
  flex: 1;
  row-gap: 16px;
}
.calendar-fullscreen .day-circle {
  width: 64px;
  height: 64px;
  font-size: 22px;
}

/* PO Indicator Styling */
.day-wrapper {
  position: relative;
}
.has-po {
  background: #e9ecef !important;
  color: #495057 !important;
  position: relative;
}
.has-po:hover {
  background: #adb5bd !important;
  color: #212529 !important;
}
.clickable {
  cursor: pointer;
}
.po-indicator {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 6px;
  height: 6px;
  background: #28a745;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(40, 167, 69, 0.5);
}
.today.has-po .po-indicator {
  background: #fff;
  box-shadow: 0 0 4px rgba(255, 255, 255, 0.5);
}

/* Tooltip Styling */
.po-tooltip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
  min-width: 200px;
  max-width: 280px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  z-index: 100;
  animation: tooltipFade 0.2s ease-out;
  overflow: hidden;
}
@keyframes tooltipFade {
  from { opacity: 0; transform: translateX(-50%) translateY(-5px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}
.tooltip-header {
  background: #28a745;
  color: white;
  padding: 10px 14px;
  font-size: 13px;
  display: flex;
  align-items: center;
}
.tooltip-body {
  padding: 10px 14px;
}
.tooltip-item {
  display: flex;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f1f3f5;
}
.tooltip-item:last-child {
  border-bottom: none;
}
.tooltip-dot {
  width: 8px;
  height: 8px;
  background: #adb5bd;
  border-radius: 50%;
  margin-right: 10px;
  flex-shrink: 0;
}
.tooltip-text {
  font-size: 12px;
  color: #495057;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tooltip-more {
  font-size: 11px;
  color: #6c757d;
  font-style: italic;
  padding-top: 6px;
  text-align: center;
}

/* Fullscreen tooltip adjustments */
.calendar-fullscreen .po-tooltip {
  min-width: 260px;
}
.calendar-fullscreen .tooltip-header {
  padding: 12px 18px;
  font-size: 15px;
}
.calendar-fullscreen .tooltip-body {
  padding: 12px 18px;
}
.calendar-fullscreen .tooltip-text {
  font-size: 14px;
}
</style>
